import { useState } from "react";
import { ChevronDownIcon, InfoIcon, SlidersHorizontalIcon } from "lucide-react";

import { Column, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import { DEFAULT_SEARCH_WEIGHTS } from "@/lib/constants";
import { searchV2Analytics } from "@/lib/searchV2Analytics";
import { cn } from "@/lib/utils";
import { SearchV2Weights } from "@/types";

const SEMANTIC_WEIGHT_MAX = 1;
const SEMANTIC_WEIGHT_MIN = 0;
const SEMANTIC_WEIGHT_STEP = 0.25;

const ACTIVITY_WEIGHT_OPTIONS = [
  { label: "Off", weight: 0 },
  { label: "Boost", weight: 0.15 },
  { label: "Prioritize", weight: 0.3 },
];

const WE_RECENCY_WEIGHT_OPTIONS = [
  { label: "Off", weight: 0 },
  { label: "Boost", weight: 0.25 },
  { label: "Prioritize", weight: 0.5 },
];

export const SearchV2TuningPopover = ({
  jobId,
  onWeightsChange,
  weights,
}: {
  jobId: string;
  onWeightsChange: (weights: SearchV2Weights) => void;
  weights: SearchV2Weights;
}) => {
  /* React Hooks */
  const [draftWeights, setDraftWeights] = useState(weights);
  const [isOpen, setIsOpen] = useState(false);

  /* Helpers */
  const changeCount =
    Number(weights.semanticWeight !== DEFAULT_SEARCH_WEIGHTS.semanticWeight) +
    Number(weights.activityWeight !== DEFAULT_SEARCH_WEIGHTS.activityWeight) +
    Number(weights.weRecencyWeight !== DEFAULT_SEARCH_WEIGHTS.weRecencyWeight);

  const getMatchStyleLabel = () => {
    const semanticPercentage = draftWeights.semanticWeight * 100;
    if (semanticPercentage < 20) return "Exact";
    if (semanticPercentage <= 44) return "Leaning exact";
    if (semanticPercentage <= 55) return "Balanced";
    if (semanticPercentage <= 80) return "Leaning broad";
    return "Broad";
  };

  const getActivityDescription = () => {
    return (
      {
        0: "Recent activity on Laborup doesn't affect ranking.",
        0.15: "Recently active candidates rank higher.",
        0.3: "Recently active candidates rank at the top.",
      }[draftWeights.activityWeight] ?? ""
    );
  };

  const getWeRecencyDescription = () => {
    return (
      {
        0: "Recent matching experience doesn't affect ranking.",
        0.25: "Recent work experience will rank higher.",
        0.5: "Recent work experience will rank at the top.",
      }[draftWeights.weRecencyWeight] ?? ""
    );
  };

  const getExtremeMatchStyleWarning = () => {
    if (draftWeights.semanticWeight === SEMANTIC_WEIGHT_MIN) {
      return "Full Exact relies only on the specific words in your search. Related experience described with different wording may be missed.";
    }
    if (draftWeights.semanticWeight === SEMANTIC_WEIGHT_MAX) {
      return "Full Broad relies only on related meaning. Candidates may rank highly even when they do not mention your exact requirements.";
    }
    return null;
  };

  /* Handlers */
  const handleWeightsChange = (partialWeights: Partial<SearchV2Weights>) => {
    setDraftWeights((currentWeights) => ({
      ...currentWeights,
      ...partialWeights,
    }));
  };

  // Apply draft weights only when the popover closes so we don't fire a
  // search request on every slider tick.
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      setDraftWeights(weights);
      return;
    }
    if (
      draftWeights.semanticWeight === weights.semanticWeight &&
      draftWeights.activityWeight === weights.activityWeight &&
      draftWeights.weRecencyWeight === weights.weRecencyWeight
    ) {
      return;
    }
    // One event per changed weight so each slider charts on its own.
    searchV2Analytics.trackWeightChanges({
      jobId,
      nextWeights: draftWeights,
      previousWeights: weights,
    });
    onWeightsChange(draftWeights);
  };

  const handleSemanticWeightChange = (value: string) => {
    const semanticWeight = Number(value);
    handleWeightsChange({
      semanticWeight,
      textWeight: 1 - semanticWeight,
    });
  };

  /* Render */
  const renderTrigger = () => {
    return (
      <PopoverTrigger asChild>
        <Button
          className="relative h-12 shrink-0 gap-2 rounded-[18px] border-slate-200 bg-slate-50 px-4 text-slate-600 hover:bg-slate-100 hover:text-slate-700"
          type="button"
          variant="outline"
        >
          <SlidersHorizontalIcon className="h-4 w-4" />
          Tune
          <ChevronDownIcon className="h-4 w-4" />
          {changeCount > 0 && (
            <Row className="absolute -right-1 -top-1 h-4 min-w-4 items-center justify-center rounded-full bg-blue-600 px-1">
              <Text className="text-[9px] font-semibold leading-none text-white">
                {changeCount}
              </Text>
            </Row>
          )}
        </Button>
      </PopoverTrigger>
    );
  };

  const renderMatchStyleSection = () => {
    const extremeMatchStyleWarning = getExtremeMatchStyleWarning();

    return (
      <Column className="gap-3 px-4 py-3">
        <Row className="items-center justify-between">
          <Text className="text-sm font-medium text-slate-900">
            Match style
          </Text>
          <Text className="font-plex-mono text-[11px] font-semibold uppercase tracking-wide text-blue-700">
            {getMatchStyleLabel()}
          </Text>
        </Row>
        <input
          className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-blue-600"
          max={SEMANTIC_WEIGHT_MAX}
          min={SEMANTIC_WEIGHT_MIN}
          onChange={(event) => {
            handleSemanticWeightChange(event.target.value);
          }}
          step={SEMANTIC_WEIGHT_STEP}
          type="range"
          value={draftWeights.semanticWeight}
        />
        <Row className="items-start justify-between gap-6">
          <Column className="min-w-0 flex-1 gap-0.5">
            <Text className="text-sm font-medium text-slate-600">Exact</Text>
            <Text className="text-xs font-normal leading-4 text-slate-500">
              Hard skills, machines, or requirements.
            </Text>
          </Column>
          <Column className="min-w-0 flex-1 items-end gap-0.5 text-right">
            <Text className="text-sm font-medium text-slate-600">Broad</Text>
            <Text className="text-xs font-normal leading-4 text-slate-500">
              Related experience or transferable skills.
            </Text>
          </Column>
        </Row>
        {extremeMatchStyleWarning ? (
          <Row className="items-start gap-2 rounded-xl border border-blue-200 bg-blue-50 p-2.5">
            <InfoIcon className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
            <Text className="text-xs font-normal leading-relaxed text-blue-800">
              {extremeMatchStyleWarning}
            </Text>
          </Row>
        ) : null}
      </Column>
    );
  };

  const renderBoostOptions = (
    options: { label: string; weight: number }[],
    selectedWeight: number,
    onSelect: (weight: number) => void,
  ) => {
    return (
      <Row className="items-center overflow-hidden rounded-xl border border-slate-200">
        {options.map((option, index) => (
          <Button
            className={cn(
              "h-9 flex-1 rounded-none border-0 border-l border-slate-200 px-1.5 text-sm font-normal text-slate-600 shadow-none hover:bg-slate-50 hover:text-slate-800",
              index === 0 && "border-l-0",
              selectedWeight === option.weight &&
                "bg-blue-100 font-medium text-blue-800 hover:bg-blue-100 hover:text-blue-800",
            )}
            key={option.label}
            onClick={() => {
              onSelect(option.weight);
            }}
            type="button"
            variant="ghost"
          >
            {option.label}
          </Button>
        ))}
      </Row>
    );
  };

  const renderActivitySection = () => {
    return (
      <Column className="gap-2 px-4 pb-3 pt-2">
        <Text className="text-sm font-semibold text-slate-900">
          Recent activity
        </Text>
        {renderBoostOptions(
          ACTIVITY_WEIGHT_OPTIONS,
          draftWeights.activityWeight,
          (weight) => {
            handleWeightsChange({ activityWeight: weight });
          },
        )}
        <Text className="text-xs font-normal leading-4 text-slate-500">
          {getActivityDescription()}
        </Text>
      </Column>
    );
  };

  const renderWeRecencySection = () => {
    return (
      <Column className="gap-2 px-4 py-2">
        <Text className="text-sm font-semibold text-slate-900">
          Recent work experience
        </Text>
        {renderBoostOptions(
          WE_RECENCY_WEIGHT_OPTIONS,
          draftWeights.weRecencyWeight,
          (weight) => {
            handleWeightsChange({ weRecencyWeight: weight });
          },
        )}
        <Text className="text-xs font-normal leading-4 text-slate-500">
          {getWeRecencyDescription()}
        </Text>
      </Column>
    );
  };

  const render = () => {
    return (
      <Popover onOpenChange={handleOpenChange} open={isOpen}>
        {renderTrigger()}
        <PopoverContent
          align="end"
          className="w-80 overflow-hidden rounded-2xl p-0"
        >
          <Column>
            <Row className="items-center justify-between px-4 pb-2 pt-4">
              <Text className="text-base font-semibold text-slate-900">
                Tune results
              </Text>
              <Button
                className="h-auto p-0 text-xs font-normal text-slate-500 hover:bg-transparent hover:text-slate-800"
                onClick={() => {
                  handleWeightsChange(DEFAULT_SEARCH_WEIGHTS);
                }}
                type="button"
                variant="ghost"
              >
                Reset
              </Button>
            </Row>
            {renderMatchStyleSection()}
            {renderWeRecencySection()}
            {renderActivitySection()}
          </Column>
        </PopoverContent>
      </Popover>
    );
  };

  return render();
};
